/**
 * /forgot-password — request a password reset link.
 *
 * Public route. Always returns "ok" so attackers cannot enumerate accounts.
 */

import { json, type ActionFunctionArgs, type LoaderFunctionArgs } from '@remix-run/cloudflare';
import { Form, Link, useActionData, useNavigation } from '@remix-run/react';
import { Mail } from 'lucide-react';
import { z } from 'zod';

import { getSession } from '~/lib/auth.server';
import { requestPasswordReset } from '~/lib/password-reset.server';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';

const schema = z.object({ email: z.string().email() });

export async function loader({ request, context }: LoaderFunctionArgs) {
  const session = await getSession(request, context.env);
  if (session) {
    return new Response(null, { status: 302, headers: { Location: '/settings/password' } });
  }
  return null;
}

export async function action({ request, context }: ActionFunctionArgs) {
  const fd = await request.formData();
  const parsed = schema.safeParse({ email: fd.get('email') });
  if (!parsed.success) {
    return json({ success: false, error: 'Email inválido.' }, { status: 400 });
  }
  await requestPasswordReset(context.env, parsed.data.email, context.env.SITE_URL);
  return json({ success: true, error: null });
}

export default function ForgotPasswordPage() {
  const actionData = useActionData<typeof action>();
  const nav = useNavigation();
  const submitting = nav.state === 'submitting';

  if (actionData?.success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-sm">
          <CardContent className="pt-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Verifique seu email</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Se houver uma conta com esse email, enviamos um link de redefinição.
              O link expira em 30 minutos.
            </p>
            <p className="mt-4 text-xs text-muted-foreground">
              <Link to="/login" className="underline">Voltar ao login</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle>Esqueci minha senha</CardTitle>
          <CardDescription>Informe seu email para receber um link de redefinição.</CardDescription>
        </CardHeader>
        <CardContent>
          <Form method="post" className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium">Email</label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
                autoFocus
              />
            </div>
            {actionData && 'error' in actionData && actionData.error && (
              <p className="text-sm text-destructive">{actionData.error}</p>
            )}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Enviando...' : 'Enviar link de redefinição'}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              <Link to="/login" className="underline">Voltar ao login</Link>
            </p>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
